import { useEffect, useCallback } from 'react'
import { ChatAction, ChatState, Tag, ChatRequest } from '../../state/types'
import { pruneTags, buildChatMetadata } from '../../utils/chat/mentions'

export function useChatComposer(
  state: ChatState,
  dispatch: React.Dispatch<ChatAction>,
  conversationId?: string
) {
  const setInput = useCallback((value: string) => {
    dispatch({ type: 'SET_INPUT', payload: value })
    
    // Prune tags that are no longer in the text
    const pruned = pruneTags(state.tags, value)
    if (pruned.length !== state.tags.length) {
      dispatch({ type: 'SET_TAGS', payload: pruned })
    }
  }, [dispatch, state.tags])

  const addMention = useCallback((mention: Tag) => {
    if (!state.tags.some(t => t.id === mention.id)) {
      dispatch({ type: 'SET_TAGS', payload: [...state.tags, mention] })
    }
  }, [dispatch, state.tags])

  const removeMention = useCallback((id: string) => {
    dispatch({ type: 'SET_TAGS', payload: state.tags.filter(t => t.id !== id) })
  }, [dispatch, state.tags])

  const buildRequest = useCallback((message: string, overrideTags?: Tag[]): ChatRequest => {
    const tagsToUse = overrideTags ?? state.tags
    const metadata = buildChatMetadata(tagsToUse)
    return metadata ? { message, metadata } : { message }
  }, [state.tags])

  const clearInput = useCallback(() => {
    dispatch({ type: 'CLEAR_INPUT' })
  }, [dispatch])

  // Draft persistence
  useEffect(() => {
    if (!conversationId) return
    
    const key = `chat:draft:${conversationId}`
    const saved = localStorage.getItem(key)
    
    if (saved && !state.inputValue) {
      dispatch({ type: 'SET_INPUT', payload: saved })
    }
  }, [conversationId, dispatch, state.inputValue])

  useEffect(() => {
    if (!conversationId) return
    
    const key = `chat:draft:${conversationId}`
    if (state.inputValue) {
      localStorage.setItem(key, state.inputValue)
    } else {
      localStorage.removeItem(key)
    }
  }, [conversationId, state.inputValue])

  return {
    setInput,
    addMention,
    removeMention,
    buildRequest,
    clearInput
  }
} 